import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);  
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    // Use navigate if possible, but window.location works for a hard reset
    window.location.href = '/login';
  };

  /**
   * Updated login to accept both token and user data.
   * This ensures the UI has the most up-to-date 'approved' status 
   * immediately without waiting for a token decode.
   */
  const login = (token, userData) => {
    localStorage.setItem('token', token);
    
    if (userData) {
      // If backend sends the user object directly
      setCurrentUser(userData);
    } else {
      // Fallback: decode from token
      const decoded = jwtDecode(token);
      setCurrentUser({
        id: decoded.id,
        role: decoded.role,
        email: decoded.email,
        approved: decoded.approved, // Added
        is_complete: decoded.is_complete // Added
      });
    }
  };

const updateProfileData = async (formData) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error("No authentication token found");

    try {
      // 1. Send the updated data to your backend API route
      // (Change '/api/user/profile' to match your actual backend URL path)
      const response = await fetch('/api/user/profile', {
        method: 'PUT', // or 'PATCH' depending on your backend
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Sends your JWT token to authorize the request
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update profile on server");
      }

      const updatedUserFromServer = await response.json();

      // 2. Update local state so your UI refreshes immediately
      setCurrentUser(prevUser => ({
        ...prevUser,
        ...updatedUserFromServer // Merges the new fields into your existing user state
      }));

    } catch (error) {
      console.error("Error updating profile in AuthContext:", error);
      throw error; // Propagates error back to your form component's catch block
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Expiry check
        if (decoded.exp * 1000 < Date.now()) {
          logout();
        } else {
          setCurrentUser({
            id: decoded.id,
            role: decoded.role,
            email: decoded.email,
            approved: decoded.approved, // Added
            is_complete: decoded.is_complete // Added
          });
        }
      } catch (error) {
        console.error("Token verification failed");
        logout();
      }
    }
    setLoading(false);
  }, []);

  // Helper properties for easy role validation
  const isAdmin = currentUser?.role === 'admin';
  const isTutor = currentUser?.role === 'tutor';
  const isLearner = currentUser?.role === 'learner';

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      login, 
      logout, 
      loading, 
      updateProfileData,
      isAdmin,
      isTutor,
      isLearner 
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;